// export { default } from './UsersPage'

import { Stack, Tabs } from '@mantine/core'
import { lazy, Suspense, useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryParamTab } from 'src/utils/tabQueryParam'
import { buildUsersTabSearchParams } from 'src/utils/usersTabQuery'
import { TabPanelSkeleton, UserTableSkeleton } from 'src/containers/Users/UserTableSkeleton'

const UsersPage = lazy(() => import('./UsersPage'))
const GuidersPage = lazy(() => import('src/pages/GuidersPage'))
const EmployeesPage = lazy(() => import('src/pages/EmployeePage/EmployeesPage'))

type UsersTabValue = 'user' | 'guide' | 'employee'

function UsersTabsPage() {
	const [searchParams, setSearchParams] = useSearchParams()
	const userRole = useMemo(() => {
		try {
			const info = JSON.parse(localStorage.getItem('info') || '{}')
			return info?.role
		} catch (error) {
			return undefined
		}
	}, [])
	const canViewEmployeeCms = userRole === 'SUPERADMIN'
	const tabValues = useMemo<readonly UsersTabValue[]>(
		() => (
			canViewEmployeeCms
				? (['user', 'guide', 'employee'] as const)
				: (['user', 'guide'] as const)
		),
		[canViewEmployeeCms],
	)
	const { activeTab } = useQueryParamTab(
		tabValues,
		'user',
	)

	const handleUsersTabChange = useCallback(
		(value: string | null) => {
			if (!value || !tabValues.includes(value as UsersTabValue)) {
				return
			}

			const nextTab = value as UsersTabValue
			setSearchParams(
				(previousParams) => buildUsersTabSearchParams(previousParams, nextTab),
				{ replace: true },
			)
		},
		[setSearchParams, tabValues],
	)

	useEffect(() => {
		if (canViewEmployeeCms || searchParams.get('tab') !== 'employee') {
			return
		}

		setSearchParams((prev) => {
			const nextSearchParams = new URLSearchParams(prev)
			nextSearchParams.set('tab', 'user')
			return nextSearchParams
		}, { replace: true })
	}, [canViewEmployeeCms, searchParams, setSearchParams])

	return (
		<Stack
			sx={{
				width: '100%',
			}}
		>
			<Tabs value={activeTab} onTabChange={handleUsersTabChange}>
				<Tabs.List>
					<Tabs.Tab value="user" fw={700}>User App</Tabs.Tab>
					<Tabs.Tab value="guide" fw={700}>Guide</Tabs.Tab>
					{canViewEmployeeCms && (
						<Tabs.Tab value="employee" fw={700}>Employee CMS</Tabs.Tab>
					)}
				</Tabs.List>
			</Tabs>

			<div role="tabpanel" style={{ paddingTop: 16, width: '100%' }}>
				{activeTab === 'user' && (
					<Suspense fallback={<UserTableSkeleton />}>
						<UsersPage />
					</Suspense>
				)}
				{activeTab === 'guide' && (
					<Suspense fallback={<TabPanelSkeleton />}>
						<GuidersPage />
					</Suspense>
				)}
				{activeTab === 'employee' && canViewEmployeeCms && (
					<Suspense fallback={<TabPanelSkeleton />}>
						<EmployeesPage />
					</Suspense>
				)}
			</div>
		</Stack>
	)
}

export default UsersTabsPage
